import { useEffect, type ReactNode } from 'react';

/**
 * A React-controlled Bootstrap modal.
 *
 * The Razor pages drove modals with data-bs-* attributes and bootstrap.bundle.js. Doing that
 * from React means the library mutates DOM that React also owns, which breaks on re-render, so
 * the markup is rendered directly in the shown state instead - no Bootstrap JS needed.
 */
interface ModalProps {
  show: boolean;
  title?: ReactNode;
  onClose: () => void;
  size?: 'sm' | 'lg' | 'xl';
  centered?: boolean;
  /** "center" (default) suits short confirm dialogs; forms use "start". */
  align?: 'center' | 'start';
  children: ReactNode;
  footer?: ReactNode;
}

export function Modal({ show, title, onClose, size, centered = true, align = 'center', children, footer }: ModalProps) {
  // Escape closes, and the body scrollbar is suppressed while open - both are behaviours
  // Bootstrap's own JS would normally provide.
  useEffect(() => {
    if (!show) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.classList.add('modal-open');

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.classList.remove('modal-open');
    };
  }, [show, onClose]);

  if (!show) return null;

  const sizeClass = size ? ` modal-${size}` : '';

  return (
    <>
      <div
        className="modal fade show"
        style={{ display: 'block' }}
        tabIndex={-1}
        role="dialog"
        onClick={onClose}
      >
        <div
          className={`modal-dialog${sizeClass}${centered ? ' modal-dialog-centered' : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            {title !== undefined && (
              <div className="modal-header py-2">
                <h6 className="modal-title mb-0">{title}</h6>
                <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
              </div>
            )}
            <div className={align === 'start' ? 'modal-body text-start p-4' : 'modal-body text-center p-3'}>
              {children}
            </div>
            {footer && (
              <div className={align === 'start' ? 'modal-footer' : 'modal-footer border-0 pt-0'}>{footer}</div>
            )}
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  );
}
